import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/brand/Header";
import { Footer } from "@/components/brand/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Calendar, Plane, Users, CheckCircle2, Mountain, Coffee, Sparkles, Lock } from "lucide-react";
import { CheckoutDialog, ReceiptOverlay } from "@/components/brand/CheckoutDialog";

export default function TourDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, tData, locale, currency, convertPrice, formatPrice } = useLocale();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    selected_date: "",
    origin_airport: "",
    passengers: 2,
    notes: "",
    passengers_info: [
      { full_name: "", document_type: "passport", document_number: "", document_country: "" },
      { full_name: "", document_type: "passport", document_number: "", document_country: "" },
    ],
  });
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (user === false) {
      navigate(`/login?next=/tours/${id}`);
      return;
    }
    if (user.role === "buyer" && user.status !== "approved") {
      navigate("/pending");
      return;
    }
    setLoading(true);
    api.get(`/tours/${id}`)
      .then((r) => {
        setTour(r.data);
        // Preselect the first available date if any
        const firstDate = (r.data.available_dates || [])[0]?.date;
        if (firstDate) setForm((f) => ({ ...f, selected_date: firstDate }));
      })
      .catch(() => toast.error(t("tours.not_found")))
      .finally(() => setLoading(false));
  }, [id, user, navigate, t]);

  const localized = (key) => {
    if (!tour) return "";
    if (locale === "en" && tour[`${key}_en`]) return tour[`${key}_en`];
    if (locale === "es" && tour[`${key}_es`]) return tour[`${key}_es`];
    return tour[key];
  };

  const tourDays = tour?.duration_days || (tour?.duration_options || [7])[0];

  const pricePerPerson = useMemo(() => {
    if (!tour) return 0;
    if (tour.price_per_person_usd) return tour.price_per_person_usd;
    // Legacy fallback (proportional pricing)
    const base = tour.price_per_person_7d_usd || 1000;
    return Math.round(base * (tourDays / 7) * 100) / 100;
  }, [tour, tourDays]);

  const totalPrice = useMemo(() => {
    if (form.passengers === 1) {
      // Single-supplement: +50% on price/person when traveling solo
      return Math.round(pricePerPerson * 1.5 * 100) / 100;
    }
    return Math.round(pricePerPerson * form.passengers * 100) / 100;
  }, [pricePerPerson, form.passengers]);

  const singleSupplementApplied = form.passengers === 1;
  const supplementAmount = singleSupplementApplied
    ? Math.round(pricePerPerson * 0.5 * 100) / 100
    : 0;

  // Sync passengers_info length with passengers
  const setPassengersCount = (n) => {
    const next = Math.min(20, Math.max(1, Number(n) || 1));
    setForm((f) => {
      const arr = [...(f.passengers_info || [])];
      while (arr.length < next) arr.push({ full_name: "", document_type: "passport", document_number: "", document_country: "" });
      arr.length = next;
      return { ...f, passengers: next, passengers_info: arr };
    });
  };

  const updatePassenger = (idx, field, value) => {
    setForm((f) => {
      const arr = [...(f.passengers_info || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...f, passengers_info: arr };
    });
  };

  const selectedDateInfo = useMemo(() => {
    if (!tour || !form.selected_date) return null;
    return (tour.available_dates || []).find((d) => d.date === form.selected_date) || null;
  }, [tour, form.selected_date]);

  const seatsRemaining = selectedDateInfo?.seats ?? null;
  const seatsOk = seatsRemaining == null || seatsRemaining >= form.passengers;

  const openCheckout = () => {
    if (!form.origin_airport) {
      toast.error(t("tours.airport"));
      return;
    }
    if ((tour.available_dates || []).length > 0 && !form.selected_date) {
      toast.error(t("tours.pick_date_first"));
      return;
    }
    if (!seatsOk) {
      toast.error(t("tours.not_enough_seats"));
      return;
    }
    // Validate every passenger has name + document
    const invalid = (form.passengers_info || []).findIndex(
      (p) => !p.full_name?.trim() || !p.document_number?.trim()
    );
    if (invalid >= 0 || (form.passengers_info || []).length !== form.passengers) {
      toast.error(t("tours.error_passenger_info"));
      return;
    }
    setCheckoutOpen(true);
  };

  const onPaymentSuccess = (data) => {
    setReceipt(data);
  };

  if (loading || !tour) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <div className="flex-1 flex items-center justify-center text-muted-foreground font-mono-data text-xs uppercase tracking-wider">
          {t("common.loading")}
        </div>
        <Footer />
      </div>
    );
  }

  const fmt = (n) => formatPrice(convertPrice(n, "USD", currency), currency);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1">
        {/* Hero with image */}
        <section className="relative">
          <div className="relative aspect-[21/9] max-h-[520px] overflow-hidden">
            <img src={tour.hero_image} alt={localized("name")} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0">
              <div className="mx-auto max-w-[1500px] px-4 sm:px-8 pb-10">
                <Link to="/tours" className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-accent flex items-center gap-1.5 mb-4">
                  <ArrowLeft className="h-3 w-3" /> {t("nav.tours")}
                </Link>
                <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-accent mb-3 flex items-center gap-2">
                  <MapPin className="h-3 w-3" /> {tData("region", tour.region)} · {tour.country}
                </div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black leading-[0.95] max-w-3xl">
                  {localized("name")}
                </h1>
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-[1500px] w-full px-4 sm:px-8 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left content */}
          <div className="lg:col-span-7">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">{t("tours.summary")}</div>
            <p className="text-lg text-foreground leading-relaxed text-pretty">{localized("summary")}</p>

            {/* Long description (new) */}
            {localized("description") && (
              <div className="mt-8">
                <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">{t("tours.about_label")}</div>
                <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-line text-pretty">{localized("description")}</p>
              </div>
            )}

            {/* Highlights */}
            <div className="mt-12">
              <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">{t("tours.highlights")}</div>
              <ul className="space-y-3">
                {(localized("highlights") || []).map((h, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <Sparkles className="h-4 w-4 text-accent shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Farms */}
            <div className="mt-12">
              <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">{t("tours.farms_visited")}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {tour.farms?.map((f, i) => (
                  <div key={i} className="border border-border bg-card p-5 hover-lift">
                    <div className="flex items-center gap-2 text-accent mb-2">
                      <Coffee className="h-4 w-4" strokeWidth={1.5} />
                      <span className="font-mono-data text-[10px] uppercase tracking-wider">{f.city}</span>
                    </div>
                    <div className="font-display text-lg tracking-tight">{f.name}</div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5"><Mountain className="h-3 w-3" /> {f.altitude}</div>
                    </div>
                    <div className="mt-2 text-xs">
                      <span className="text-muted-foreground">{t("tours.specialty")}:</span> <span className="font-medium">{f.specialty}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Includes */}
            <div className="mt-12">
              <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">{t("tours.includes_title")}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(localized("includes") || []).map((inc, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span>{inc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Gallery (legacy support — hidden if no gallery field) */}
            {(tour.gallery || []).length > 0 && (
              <div className="mt-12">
                <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">{t("tours.gallery")}</div>
                <div className="grid grid-cols-2 gap-2">
                  {(tour.gallery || []).slice(0, 4).map((img, i) => (
                    <div key={i} className="aspect-[4/3] overflow-hidden border border-border">
                      <img src={img} alt="" className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Season */}
            <div className="mt-12 border border-border bg-secondary/30 p-6">
              <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">{t("tours.best_season")}</div>
              <div className="flex flex-wrap gap-2">
                {tour.best_seasons?.map((s, i) => (
                  <span key={i} className="px-2 py-0.5 border border-border bg-background text-xs font-mono-data uppercase">{s}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Request form */}
          <aside className="lg:col-span-5">
            <div className="sticky top-24 border border-border bg-card p-7 shadow-sm">
              <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-accent mb-2">{t("tours.estimate")}</div>
              <div className="flex items-baseline gap-2">
                <div className="font-display text-5xl tracking-tighter font-black text-accent">{fmt(totalPrice)}</div>
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                <span className="font-mono-data text-xs">{fmt(pricePerPerson)} {t("tours.per_person")}</span> · {form.passengers} {t("tours.pax_short")} · {tourDays} {t("tours.days")}
              </div>
              {singleSupplementApplied && (
                <div
                  data-testid="tour-supplement-breakdown"
                  className="mt-3 border-t border-border pt-3 space-y-1 text-[11px] font-mono-data"
                >
                  <div className="flex justify-between text-muted-foreground">
                    <span>{t("tours.subtotal")}</span>
                    <span>{fmt(pricePerPerson)}</span>
                  </div>
                  <div className="flex justify-between text-amber-700 dark:text-amber-300">
                    <span>{t("tours.supplement_line")}</span>
                    <span>+ {fmt(supplementAmount)}</span>
                  </div>
                </div>
              )}

              <div className="mt-6 space-y-4">
                {/* Available dates picker */}
                {(tour.available_dates || []).length > 0 && (
                  <div>
                    <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
                      <Calendar className="h-3 w-3" /> {t("tours.choose_date")}
                    </Label>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {(tour.available_dates || []).map((d) => {
                        const isActive = form.selected_date === d.date;
                        const isFull = (d.seats ?? 0) < 1;
                        return (
                          <button
                            key={d.date}
                            type="button"
                            disabled={isFull}
                            data-testid={`tour-date-${d.date}`}
                            onClick={() => setForm({ ...form, selected_date: d.date })}
                            className={`px-3 py-2 border text-left font-mono-data text-xs transition-colors ${
                              isFull
                                ? "border-border bg-muted text-muted-foreground/40 cursor-not-allowed"
                                : isActive
                                  ? "border-accent bg-accent text-accent-foreground"
                                  : "border-border bg-background hover:border-accent/60"
                            }`}
                          >
                            <div className="text-sm font-semibold tracking-tight">{d.date}</div>
                            <div className="text-[10px] uppercase tracking-wider opacity-80">
                              {d.seats} {t("tours.seats_left")}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {selectedDateInfo && !seatsOk && (
                      <p className="text-[10px] text-destructive mt-1.5" data-testid="tour-not-enough-seats">
                        {t("tours.not_enough_seats")}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
                    <Plane className="h-3 w-3" /> {t("tours.airport")}
                  </Label>
                  <Select value={form.origin_airport} onValueChange={(v) => setForm({ ...form, origin_airport: v })}>
                    <SelectTrigger className="mt-2 h-11 rounded-none">
                      <SelectValue placeholder={t("tours.airport")} />
                    </SelectTrigger>
                    <SelectContent>
                      {tour.supported_airports?.map((a) => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1.5">{t("tours.airport_help")}</p>
                </div>

                <div>
                  <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
                    <Users className="h-3 w-3" /> {t("tours.passengers")}
                  </Label>
                  <Input
                    type="number" min={1} max={20}
                    value={form.passengers}
                    onChange={(e) => setPassengersCount(e.target.value)}
                    data-testid="tour-passengers-input"
                    className="mt-2 h-11 rounded-none"
                  />
                  {singleSupplementApplied && (
                    <div
                      data-testid="tour-single-supplement-notice"
                      className="mt-2 border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] leading-snug text-amber-900 dark:text-amber-200"
                    >
                      <span className="font-mono-data uppercase tracking-wider text-[9px] block mb-1">
                        {t("tours.single_supplement_label")}
                      </span>
                      {t("tours.single_supplement_note")}
                    </div>
                  )}
                </div>

                {/* Passenger details (name + document) */}
                <div className="border-t border-border pt-4">
                  <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {t("tours.passenger_info_title")}
                  </Label>
                  <p className="text-[10px] text-muted-foreground/80 mt-1 leading-snug">
                    {t("tours.passenger_info_subtitle")}
                  </p>
                  <div className="mt-3 space-y-3">
                    {(form.passengers_info || []).map((p, idx) => (
                      <div
                        key={idx}
                        data-testid={`tour-passenger-card-${idx}`}
                        className="border border-border bg-secondary/20 p-3 space-y-2"
                      >
                        <div className="font-mono-data text-[10px] uppercase tracking-wider text-accent">
                          {t("tours.passenger_n").replace("{n}", String(idx + 1))}
                        </div>
                        <Input
                          value={p.full_name}
                          onChange={(e) => updatePassenger(idx, "full_name", e.target.value)}
                          placeholder={t("tours.field_full_name")}
                          data-testid={`tour-passenger-${idx}-name`}
                          className="h-10 rounded-none text-sm"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <Select
                            value={p.document_type}
                            onValueChange={(v) => updatePassenger(idx, "document_type", v)}
                          >
                            <SelectTrigger
                              data-testid={`tour-passenger-${idx}-doc-type`}
                              className="h-10 rounded-none text-xs col-span-1"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="passport">{t("tours.doc_passport")}</SelectItem>
                              <SelectItem value="cpf">{t("tours.doc_cpf")}</SelectItem>
                              <SelectItem value="rg">{t("tours.doc_rg")}</SelectItem>
                              <SelectItem value="other">{t("tours.doc_other")}</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            value={p.document_number}
                            onChange={(e) => updatePassenger(idx, "document_number", e.target.value)}
                            placeholder={t("tours.field_doc_number")}
                            data-testid={`tour-passenger-${idx}-doc-number`}
                            className="h-10 rounded-none text-sm col-span-2"
                          />
                        </div>
                        {p.document_type === "passport" && (
                          <Input
                            value={p.document_country || ""}
                            onChange={(e) => updatePassenger(idx, "document_country", e.target.value)}
                            placeholder={t("tours.field_doc_country")}
                            data-testid={`tour-passenger-${idx}-doc-country`}
                            className="h-10 rounded-none text-sm"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {t("tours.notes")} ({t("common.optional")})
                  </Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder={t("tours.notes_placeholder")}
                    className="mt-2 rounded-none min-h-[90px]"
                  />
                </div>
              </div>

              <Button
                onClick={openCheckout}
                disabled={!form.origin_airport || !seatsOk || ((tour.available_dates || []).length > 0 && !form.selected_date)}
                data-testid="tour-pay-and-book"
                className="mt-6 w-full h-12 rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground font-mono-data text-xs uppercase tracking-[0.2em]"
              >
                <Lock className="h-3.5 w-3.5 mr-2" />
                {t("tours.pay_and_book")}
              </Button>

              <p className="text-[10px] text-muted-foreground mt-4 text-center">
                {t("tours.contact_disclaimer")}
              </p>
            </div>
          </aside>
        </div>
      </main>

      <Footer />

      <CheckoutDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        tour={tour}
        form={form}
        totalPrice={totalPrice}
        pricePerPerson={pricePerPerson}
        tourDays={tourDays}
        currency={currency}
        formatPrice={formatPrice}
        convertPrice={convertPrice}
        onSuccess={onPaymentSuccess}
      />
      <ReceiptOverlay
        open={!!receipt}
        onClose={() => setReceipt(null)}
        receipt={receipt}
        tour={tour}
        formatPrice={formatPrice}
        locale={locale}
      />
    </div>
  );
}
